import { EmptyContent } from "@/common/EmptyContent";
import { Pane, SplitPane } from "@/common/SplitPane";
import { RuleEditor, createRulePlugins } from "@/common/editor/RuleAuthor/RuleEditor";
import { HistoryToolbar } from "@/common/editor/RuleAuthor/toolbar/basicToolbar";
import { RulesToolbar } from "@/common/editor/RuleAuthor/toolbar/components";
import {
  type SectionData,
  SectionProvider,
  createSection,
  createSectionPlugin,
} from "@/common/editor/components/section";
import { useRelease } from "@/common/hooks";
import { scrollable } from "@/common/scrollbar";
import { ExperimentalLabel } from "@/components/meta";
import { createAppResourceService, global } from "@/global";
import { useCurrentDocument } from "@/pages/documents";
import { deserializeFuncForSuggestRulesModal } from "@/pages/documents/documents.slice.constants";
import ReleaseContextProvider from "@/pages/models/release/ReleaseContextProvider";
import { type DialogAction, DialogActions } from "@/ui/dialogs/actions";
import { useDialogModal } from "@/ui/dialogs/useDialogModal";
import styled from "@emotion/styled";
import { useDialogContext } from "@imminently/decisively-ui/Dialog";
import ToolbarBase from "@imminently/decisively-ui/Toolbar";
import { LinearProgress } from "@mui/material";
import type { Attribute } from "@packages/compiler";
import { PlateProvider, type PlateProviderProps, type TElement, type Value } from "@udecode/plate";
import { once } from "lodash";
import get from "lodash/get";
import React, { Suspense } from "react";
import { Trans, useTranslation } from "react-i18next";
import { getSourceText } from "../getSourceText";
import { SuggestRulesLeftPane } from "./LeftPane";
import { type ResponseState, getRulesFromSuccessAiResponse, isSuccessAiResponse } from "./constants";

const createRightHandSidePlugins = once(() =>
  createRulePlugins([
    createSectionPlugin({
      options: { disableSuggestModals: true },
    }),
  ]),
);

const EditorPanes = styled(SplitPane)`
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  flex: 1;
  max-height: calc(100% - 3.5rem);

  [data-slate-editor="true"] {
    padding: 1rem 0;
  }
`;

const Toolbar = styled(ToolbarBase)`
  overflow: visible hidden;
  min-height: 3.5rem;
`;

export type SuggestSection = SectionData & {
  source: TElement;
};

const StyledDialogActions = styled(DialogActions)`
  justify-content: flex-end;
  gap: 1rem;
`;

type SuggestRulesModalProps = {
  model: string;
  releaseId: string;
  sections: SuggestSection[];
  onInsert: (section: SuggestSection, rules: TElement[]) => unknown;
};

const SuggestRulesModal = ({ model, releaseId, onInsert, sections }) => {
  const dialogContext = useDialogContext();
  const { t } = useTranslation();
  const [chatResponses, setChatResponses] = React.useState<string[]>([]);

  const [sourceLocal, setSourceLocal] = React.useState<Value>(() => {
    return sections.flatMap((section) => section.source);
  });
  const sourceLocalRef = React.useRef(sourceLocal);
  if (sourceLocalRef.current !== sourceLocal) sourceLocalRef.current = sourceLocal;

  const [prompt, setPrompt] = React.useState("");
  const [promptType, setPromptType] = React.useState("");
  React.useEffect(() => {
    let url = "/auth/getPrompt";
    // Initially try and see if there is a model prompt
    if (promptType === "" || promptType === "model") url += `?model=${model}`;
    global.client(url).then((res) => {
      setPrompt(String(get(res, ["data", "content"], "")));
      const newPromptType = get(res, "data.type");
      if (promptType === "model" && newPromptType === "system") {
        // The backend has returned the prompt as system, which means we haven't saved a project specific one yet
        // Don't change
      } else setPromptType(String(get(res, ["data", "type"], "system")));
    });
  }, [promptType]);
  const [responseState, setResponseState] = React.useState<ResponseState>({ type: "initial" });

  // We do not yet support custom functions with id, workaround is use the subresource system
  const relativeReleaseService = React.useMemo(
    () => createAppResourceService<any>(`releases/${releaseId}`),
    [releaseId],
  );

  const [loadingCount, setLoadingCount] = React.useState(0);
  const [successCount, setSuccessCount] = React.useState(0);
  const [ruleBuildCount, setRuleBuildCount] = React.useState(0);
  const { isLoading: isLoadingSuggestRules, mutateAsync: mutateAsyncSuggestRules } =
    relativeReleaseService.usePostCustom("suggestRules");
  const conversationHistory = React.useRef<string[]>([]);

  const setConversationHistory = (history: string[]) => {
    conversationHistory.current = history;
  };

  const getSuccessResponse = () => {
    const possibleResponses = [
      t("ai.suggest_rules.success_message_001"),
      t("ai.suggest_rules.success_message_002"),
      t("ai.suggest_rules.success_message_003"),
      t("ai.suggest_rules.success_message_004"),
      t("ai.suggest_rules.success_message_005"),
      t("ai.suggest_rules.success_message_006"),
      t("ai.suggest_rules.success_message_007"),
      t("ai.suggest_rules.success_message_008"),
      t("ai.suggest_rules.success_message_009"),
      t("ai.suggest_rules.success_message_010"),
    ];

    const randomSelection = Math.floor(Math.random() * possibleResponses.length);

    return possibleResponses[randomSelection];
  };

  const primaryAction = React.useMemo<DialogAction>(
    () => ({
      name: t("insert"),
      onClick: () => {
        if (responseState.type === "success") {
          for (let i = 0; i < sections.length; i++) {
            const section = sections[i];
            const rules = responseState.rules[i];
            if (rules) {
              onInsert(section, rules.children as any);
            }
          }
        }

        dialogContext?.close();
      },
      disabled: isLoadingSuggestRules,
    }),
    [responseState, isLoadingSuggestRules, onInsert],
  );

  const secondaryActions = React.useMemo<DialogAction[]>(
    () => [
      {
        name: t("generate"),
        onClick: async () => {
          setLoadingCount(0);
          setSuccessCount(0);

          let newAttributes: Array<Attribute> = [];

          let errorGeneratingRules = false;
          for (const section of sections) {
            const sectionElement = sourceLocalRef.current.find((el) => el.id === section.id);
            if (!sectionElement) throw new Error(t("errors.section_missing"));

            const source = getSourceText(sectionElement);

            setLoadingCount((i) => i + 1);
            try {
              if (!source?.trim()) {
                setSuccessCount((i) => i + 1);
                setResponseState((responseState) => {
                  const existingRules = responseState.type === "success" ? responseState.rules : [];

                  const existingSectionElement = existingRules.find((el) => el.id === section.id);

                  let newRules: any[];

                  if (existingSectionElement) {
                    existingSectionElement.children = [];
                    newRules = existingRules;
                  } else {
                    const sectionElement = createSection(section.id, [
                      {
                        type: "paragraph",
                        children: [{ text: "" }],
                      },
                    ]);
                    newRules = [...existingRules, sectionElement];
                  }

                  return {
                    type: "success",
                    value: {
                      rules: [],
                    } as any,
                    rules: newRules,
                    key: String(Math.random()),
                  };
                });
                continue;
              }

              const last10Conversation = (conversationHistory.current || []).slice(-10);
              const res = await mutateAsyncSuggestRules({
                source,
                attributes: newAttributes,
                conversation: last10Conversation,
              });
              if (isSuccessAiResponse(res)) {
                newAttributes = res.newAttributes;
                let rules = deserializeFuncForSuggestRulesModal({ rules: getRulesFromSuccessAiResponse(res) })
                  .rules as TElement[];

                if (Array.isArray(rules) && rules.length === 1) {
                  const rule = rules[0];
                  if (rule.type === "section") {
                    rules = rule.children as TElement[];
                  }
                }

                setSuccessCount((i) => i + 1);
                setResponseState((responseState) => {
                  const existingRules = responseState.type === "success" ? responseState.rules : [];

                  const existingSectionElement = existingRules.find((el) => el.id === section.id);

                  let newRules: any[];

                  if (existingSectionElement) {
                    // are the rules different?
                    const existingRulesOnElement = existingSectionElement.children;
                    existingSectionElement.children = rules;
                    newRules = existingRules;
                  } else {
                    const sectionElement = createSection(section.id, rules);
                    newRules = [...existingRules, sectionElement];
                  }

                  return {
                    type: "success",
                    value: res,
                    rules: newRules,
                    key: String(Math.random()),
                  };
                });
              }
            } catch (e) {
              const errMsg = get(e, "message", t("errors.source_generation_error_1"));
              setChatResponses((prev) => [...prev, errMsg]);
              setResponseState({ type: "error" });
              errorGeneratingRules = true;
              break;
            }
          }

          if (!errorGeneratingRules) {
            setRuleBuildCount((i) => i + 1);
            setChatResponses((prev) => [...prev, getSuccessResponse()]);
          }
        },
        disabled: isLoadingSuggestRules,
        loading: isLoadingSuggestRules,
      },
    ],
    [mutateAsyncSuggestRules, responseState, isLoadingSuggestRules, conversationHistory],
  );

  const paneProps = React.useMemo(
    () => ({
      sizes: [50, 50],
      minSize: [500, 500],
      gutterStyle: () => ({ display: "block", width: "8px" }),
    }),
    [],
  );

  const rules = responseState.type === "success" ? responseState.rules : [];
  const plateProviderKey = responseState.type === "success" ? responseState.key : "0";
  const setRules = React.useCallback<NonNullable<PlateProviderProps["onChange"]>>(
    (v) => {
      setResponseState((prev) => {
        if (prev.type !== "success") return prev;

        return { ...prev, rules: v };
      });
    },
    [setResponseState],
  );

  return (
    <>
      <EditorPanes
        {...paneProps}
        snapOffset={0}
      >
        <SuggestRulesLeftPane
          sourceLocal={sourceLocal}
          setSourceLocal={setSourceLocal}
          prompt={prompt}
          setPrompt={setPrompt}
          promptType={promptType}
          setPromptType={setPromptType}
          model={model}
          showConversationOnPanel={true}
          showConversationWithSource={true}
          setConversation={setConversationHistory}
          chatResponses={chatResponses}
          onMount={() => {
            setChatResponses((prev) => [...prev, t("ai.suggest_rules.initial_autorun")]);
            (secondaryActions[0]?.onClick as (() => void) | undefined)?.();
          }}
          onRegenerate={() => (secondaryActions[0]?.onClick as (() => void) | undefined)?.()}
          disabled={isLoadingSuggestRules}
        />

        <Pane className={scrollable}>
          {(() => {
            if (responseState.type === "success") {
              return (
                <Suspense fallback={<pre>{JSON.stringify(rules, null, 2)}</pre>}>
                  <PlateProvider
                    id={"suggest-rules-editor"}
                    plugins={createRightHandSidePlugins()}
                    value={rules}
                    onChange={setRules}
                    key={plateProviderKey}
                  >
                    <Toolbar className="border-b border-border">
                      <HistoryToolbar />
                      <ToolbarBase.Divider />
                      <RulesToolbar editorId="suggest-rules-editor" />
                    </Toolbar>

                    {/* nice idea, but I don't like the look so much...too flickery...should play with the border instead... */}
                    {/* <RefreshFrame
                      count={0}
                    >
                      <RuleEditor
                        id={"suggest-rules-editor"}
                        editableProps={{ autoFocus: true, spellCheck: false }}
                      />
                    </RefreshFrame> */}
                    <RuleEditor
                      id={"suggest-rules-editor"}
                      editableProps={{ autoFocus: true, spellCheck: false }}
                    />
                  </PlateProvider>
                </Suspense>
              );
            }

            const message = (() => {
              if (isLoadingSuggestRules) return t("ai.generating_message");

              return responseState.type === "error"
                ? t("errors.source_generation_error_1")
                : t("errors.source_generation_error_2");
            })();

            return (
              <EmptyContent
                width="100px"
                messages={[message]}
              />
            );
          })()}
        </Pane>
      </EditorPanes>
      <div className="flex w-full items-center justify-between gap-4">
        <div>
          {successCount} / {sections.length}
        </div>
        <LinearProgress
          variant={isLoadingSuggestRules ? "buffer" : "determinate"}
          style={{ flex: 1 }}
          valueBuffer={(successCount / sections.length) * 100}
          value={(successCount / sections.length) * 100}
        />

        <StyledDialogActions
          primary={primaryAction}
          // secondary={secondaryActions} // this has been moved into the chat panel under the <SuggestRulesLeftPane />
        />
      </div>
    </>
  );
};

export type OpenSuggestRulesModalFArg = SuggestRulesModalProps;

export type OpenSuggestRulesModalF = (arg: OpenSuggestRulesModalFArg) => unknown;

export const useOpenSuggestRulesModal = (): OpenSuggestRulesModalF => {
  const { openDialogModal } = useDialogModal();
  const { t } = useTranslation();
  const document = useCurrentDocument();

  if (!document) throw new Error(t("errors.suggest_rules_valid_doc"));

  const release = useRelease();

  return ({ releaseId, onInsert, sections, model }) => {
    const name = sections.length === 1 ? sections[0].name : t("ai.sections");
    openDialogModal(
      {
        title: (
          <div className="flex items-center gap-4">
            <span>
              <Trans i18nKey="ai.suggest_rules_title" /> {name}
            </span>
            <ExperimentalLabel />
          </div>
        ),
        maxWidth: "xl",
      },
      <ReleaseContextProvider release={release}>
        <SectionProvider documentId={document.id}>
          <SuggestRulesModal
            releaseId={releaseId}
            model={model}
            onInsert={onInsert}
            sections={sections}
          />
        </SectionProvider>
      </ReleaseContextProvider>,
    );
  };
};
